import sounds from 'src/lib/sounds';
import { Tooltip } from './Tooltip';
import Scroll from 'src/game/components/shared/Scroll';

type ScrollTooltipOpts = {
  scroll: Scroll<any, any>;
  tooltip: Tooltip;
  offsetX?: number;
  offsetY?: number;
  onScroll?: () => void;
};

/**
 * A helper class to manage showing a tooltip for items inside a scroll view.
 */
export class ScrollTooltip<
  T extends Tooltip = Tooltip,
  S extends Scroll = Scroll,
> {
  private opts: ScrollTooltipOpts;
  private _index?: number;

  public get index() {
    return this._index;
  }

  public getTooltip() {
    return this.opts.tooltip as T;
  }

  public getScroll() {
    return this.opts.scroll as S;
  }

  constructor(opts: ScrollTooltipOpts) {
    this.opts = opts;

    const { scroll } = opts;
    const scrollView = scroll.getView();

    // Hide tooltip when starting to drag the scroll
    const baseOnDragStart = scrollView.onDragStart.bind(scrollView);
    scrollView.onDragStart = (evt) => {
      baseOnDragStart(evt);
      this.hide(true);
      this.opts.onScroll?.();
    };

    // Hide tooltip when scrolling via input (eg. mouse wheel)
    const baseOnInputScroll = scrollView.onInputScroll.bind(scrollView);
    scrollView.onInputScroll = (evt) => {
      baseOnInputScroll(evt);
      this.hide(true);
      this.opts.onScroll?.();
    };
  }

  /**
   * Show the tooltip on the item at the given index.
   */
  public async showAtItem(
    index: number,
    opts?: Parameters<T['showTooltip']>[0],
  ) {
    // If the tooltip is already shown for the same item, hide it instead.
    if (this.opts.tooltip.isShown && this.index === index) {
      sounds.sfx.play('cancel');
      await this.hide();
      return;
    }

    sounds.sfx.play('ok');
    this._index = index;
    this.reposition();
    await this.opts.tooltip.showTooltip(opts);
  }

  /**
   * Hide the tooltip.
   */
  public async hide(instant = false) {
    this._index = undefined;
    await this.opts.tooltip.hideTooltip(instant);
  }

  private reposition() {
    const { scroll, tooltip } = this.opts;

    // Skip if no index is set
    if (this.index === undefined) return;

    const scrollView = scroll.getView();
    const offsetY = scroll.getElementPosition(this.index);
    const scrollY = scrollView.getContentView().style.y;
    const elementY = offsetY + scrollY;

    // If item is within 30% from the bottom of the scroll view, show tooltip above it
    const direction = elementY < scrollView.style.height * 0.7 ? 'down' : 'up';
    tooltip.setDirection(direction);

    // Update tooltip position accordingly to the item's position, accounting for scroll offset
    tooltip.updateOpts({
      x:
        scrollView.style.x +
        (this.opts.offsetX ?? scrollView.style.width * 0.5),
      y: scrollView.style.y + elementY + (this.opts.offsetY ?? 0),
    });
  }
}
